import { Button } from '@sunwebgroup/uds-ui/button'
import { RichText } from '@sunwebgroup/uds-ui/rich-text'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from 'react-intl'
import { formatDate } from '@/scopes/account/common/formatters'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { getContactInfoStage } from '../../domain/getContactInfoStage'
import { getDaysBeforeContactInfo } from '../../domain/policies/daysBeforeContactInfo'
import { useBookingDetailsStore } from '../../infrastructure/useBookingDetailsStore'
import { getFormattedVisitingHours } from '../selectors/travelGuideSelectors'
import { BookingDetailsSection } from './BookingDetailsSection'
import styles from './ContactSection.module.css'
import { SidebarItem } from './SidebarItem'

const WHATSAPP_BASE_URL = 'https://wa.me/'

export function ContactSection({ className }: { className?: string }) {
  const { formatMessage } = useIntl()
  const brandName = useConfiguration('brandName')
  const locale = useConfiguration('locale')
  const daysBeforeContactInfo = getDaysBeforeContactInfo(brandName)
  const rawDepartureDate = useBookingDetailsStore(state => state.rawDepartureDate)
  const rawReturnDate = useBookingDetailsStore(state => state.rawReturnDate)
  const isCancelled = useBookingDetailsStore(state => state.isCancelled)
  const stage = getContactInfoStage(rawDepartureDate, rawReturnDate, daysBeforeContactInfo, isCancelled)
  const customerSupportOpeningHours = useBookingDetailsStore(state => state.customerSupportOpeningHours)
  const travelGuideContactInfo = useBookingDetailsStore(state => state.travelGuideContactInfo)
  const formattedVisitingHours = useBookingDetailsStore(getFormattedVisitingHours)

  if (stage === null) {
    return null
  }

  const title = formatMessage({ id: 'booking_details_contact_title' })

  if (stage === 'customer-support') {
    if (!customerSupportOpeningHours) {
      return null
    }
    return (
      <BookingDetailsSection isSidebar className={className} title={title}>
        <RichText innerHTML={customerSupportOpeningHours} />
      </BookingDetailsSection>
    )
  }

  if (stage === 'upcoming') {
    const upcomingMessage = daysBeforeContactInfo === 0
      ? formatMessage({ id: 'booking_details_contact_upcoming_on_departure' })
      : formatMessage({ id: 'booking_details_contact_upcoming_before_departure' }, { days: daysBeforeContactInfo })

    return (
      <BookingDetailsSection isSidebar className={className} title={title}>
        <Typography as="p" variant="body-m-regular-tight">{upcomingMessage}</Typography>
      </BookingDetailsSection>
    )
  }

  if (stage === 'travel-guide') {
    if (!travelGuideContactInfo) {
      return null
    }

    const { phoneNumber, workingHours } = travelGuideContactInfo

    return (
      <BookingDetailsSection isSidebar className={className} title={title}>
        <SidebarItem.Container className={styles.container}>
          <SidebarItem iconName="PhoneHorn" label={formatMessage({ id: 'booking_details_contact_travel_guide_phone' })}>
            {phoneNumber
              ? (
                  <div className={styles.phoneRow}>
                    <Typography as="p" variant="body-m-bold-tight">{phoneNumber}</Typography>
                    <div className={styles.actions}>
                      <Button as="a" href={`tel:${phoneNumber}`} variant="secondary" size="XS">
                        <Button.Icon glyph="PhoneHorn" />
                        {formatMessage({ id: 'booking_details_contact_travel_guide_call' })}
                      </Button>
                      <Button as="a" href={`${WHATSAPP_BASE_URL}${phoneNumber.replace(/\D/g, '')}`} variant="secondary" size="XS" target="_blank" rel="noopener noreferrer">
                        <Button.Icon glyph="LogoWhatsapp" />
                        {formatMessage({ id: 'booking_details_contact_travel_guide_whatsapp' })}
                      </Button>
                    </div>
                  </div>
                )
              : <Typography as="p" variant="body-m-regular-tight">-</Typography>}
          </SidebarItem>
          <SidebarItem iconName="Clock" label={formatMessage({ id: 'booking_details_contact_travel_guide_opening_hours' })}>
            {workingHours
              ? <RichText innerHTML={workingHours} />
              : <Typography as="p" variant="body-m-regular-tight">-</Typography>}
          </SidebarItem>
          {formattedVisitingHours.length > 0 && (
            <SidebarItem iconName="Calendar" label={formatMessage({ id: 'booking_details_contact_travel_guide_visiting_hours_title' })}>
              <div className={styles.visitingHoursContainer}>
                {formattedVisitingHours.map(group => (
                  <div key={`${group.name}-${group.location}`}>
                    {group.visitingHours.map(vh => (
                      <Typography key={`${vh.date}-${vh.startTime}-${vh.endTime}`} as="p" variant="body-m-bold-tight" className={styles.visitingHourDateTime}>
                        <span>{formatDate(vh.date, locale)}</span>
                        <span>
                          {vh.startTime}
                          {' - '}
                          {vh.endTime}
                        </span>
                      </Typography>
                    ))}
                    <Typography as="p" variant="body-s-regular-tight" className={styles.secondaryText}>
                      {[group.name, group.location].filter(Boolean).join(' | ')}
                    </Typography>
                  </div>
                ))}
              </div>
              <Typography as="p" variant="body-s-regular-tight" className={styles.secondaryText}>
                {formatMessage({ id: 'booking_details_contact_travel_guide_visiting_hours_remark' })}
              </Typography>
            </SidebarItem>
          )}
        </SidebarItem.Container>
      </BookingDetailsSection>
    )
  }

  return null
}
